import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Dashboard from "@/pages/Dashboard";
import ReportPage from "@/pages/ReportPage";
import PipelinePage from "@/pages/PipelinePage";
import TeamPage from "@/pages/TeamPage";
import PricingPage from "@/pages/PricingPage";
import AboutPage from "@/pages/AboutPage";
import SecurityPage from "@/pages/SecurityPage";
import ContactPage from "@/pages/ContactPage";
import TermsPage from "@/pages/legal/TermsPage";
import PrivacyPage from "@/pages/legal/PrivacyPage";
import RefundPage from "@/pages/legal/RefundPage";
import DisclaimerPage from "@/pages/legal/DisclaimerPage";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect session_id in URL fragment synchronously during render (not in useEffect)
  // to beat the global AuthProvider's /auth/me race.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Public marketing & legal pages */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/refund" element={<RefundPage />} />
      <Route path="/disclaimer" element={<DisclaimerPage />} />

      {/* Authenticated app surface */}
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/report/:id" element={<ProtectedRoute><ReportPage /></ProtectedRoute>} />
      <Route path="/pipeline" element={<ProtectedRoute><PipelinePage /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  // Hide the floating "Made with Emergent" badge during PDF/print export.
  // The badge has inline `style` with `!important`, so CSS alone cannot win — we
  // temporarily mutate its inline display before print and restore after.
  useEffect(() => {
    const SELECTOR = "#emergent-badge";
    const before = () => {
      const el = document.querySelector(SELECTOR);
      if (el) {
        el.dataset._prevDisplay = el.style.display || "";
        el.style.setProperty("display", "none", "important");
      }
    };
    const after = () => {
      const el = document.querySelector(SELECTOR);
      if (el) {
        const prev = el.dataset._prevDisplay;
        el.style.display = prev || "";
        delete el.dataset._prevDisplay;
      }
    };
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => {
      window.removeEventListener("beforeprint", before);
      window.removeEventListener("afterprint", after);
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SubscriptionProvider>
            <AppRouter />
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
